import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {CardScannerService} from '../card-scanner.service';
import {catchError} from 'rxjs/operators';
import {of} from 'rxjs';

// Handling the initial state of the "Create Lead" flow:
// - "Edit" state: Default state where the user can input or edit lead details.
// - "Parsing" state: Skeleton loader is displayed while the image is being processed.
// - "Error Parsing" state: Triggered when an error occurs during image summarization, e.g., invalid image format.
enum LEAD_STATE {
    EDIT = 'edit',
    PARSING = 'parsing',
    ERROR_PARSING = 'error-parsing',
}

// Handling the state of the image processing status:
// - "Default" state: The initial state when no visual changes are needed on the UI, indicating no ongoing processing or updates.
// - "Success" state: Triggered when the image is successfully processed or parsed.
// - "Invalid" state: Triggered when an error occurs during image summarization, e.g., invalid image format.
enum AFTER_IMAGE_PROCESSING {
    DEFAULT = 'default',
    SUCCESS = 'success',
    INVALID = 'invalid',
}

// Handling the state of the "Add" lead create button:
// - "Default" state: The initial state when no visual changes are needed on the UI, indicating no ongoing saving or updates.
// - "Saving" state: Triggered when the contact details are being saved or submitted, a spinner will be shown in this state.
// - "Success" state: Triggered when the contact details are successfully saved.
// - "Error" state: Triggered when an error occurs during saving the contact details.
enum LEAD_SAVE_CONTACT_STATE {
    DEFAULT = 'default',
    SAVING = 'saving',
    SUCCESS = 'success',
    ERROR = 'error',
}

@Component({
    selector: 'app-image-capture-create-card',
    templateUrl: './image-capture-create-card.component.html',
    styleUrls: ['./image-capture-create-card.component.scss'],
})
export class ImageCaptureCreateCardComponent {
    @Input() capturedImage: File;
    @Input() isMobileDevice: boolean = false;
    @Output() closeScreen: EventEmitter<'closeScreen' | 'retakeImage'> = new EventEmitter<'closeScreen' | 'retakeImage'>();
    @Output() summarizeDataEmitter: EventEmitter<any> = new EventEmitter<any>();
    @Output() onScanErrorEmitter: EventEmitter<any> = new EventEmitter<any>();

    LEAD_STATE = LEAD_STATE;
    LEAD_SAVE_CONTACT_STATE = LEAD_SAVE_CONTACT_STATE;
    AFTER_IMAGE_PROCESSING = AFTER_IMAGE_PROCESSING;

    // current state of lead capture button
    current_lead_state = LEAD_STATE.EDIT;
    save_contact_state = LEAD_SAVE_CONTACT_STATE.DEFAULT;
    image_processing_state = AFTER_IMAGE_PROCESSING.DEFAULT;

    // fields for user lead
    userLeadFields = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'designation',
        'company',
    ];

    tempCapturedURLData: string | ArrayBuffer | null = null;

    businessCardLeadFieldForm = this.formBuilder.group({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        designation: '',
        company: '',
    });

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly cardScannerService: CardScannerService
    ) {}

    ngOnInit() {
        this.updateLeadImage(this.capturedImage);
        this.uploadImage(this.capturedImage);
    }

    // Update the state of lead state
    private handleLeadState(state: LEAD_STATE) {
        this.current_lead_state = state;

        if (state === LEAD_STATE.ERROR_PARSING) {
            this.onScanErrorEmitter.emit();
        }
    }

    // Update the state of image processing status
    private handleAfterImageProcessingState(state: AFTER_IMAGE_PROCESSING) {
        this.image_processing_state = state;
        setTimeout(() => {
            this.image_processing_state = AFTER_IMAGE_PROCESSING.DEFAULT;
        }, 3000);
    }

    // Store image in temp variable for preview
    private updateLeadImage(passedLeadImage: File) {
        const reader = new FileReader();
        reader.onload = () => {
            this.tempCapturedURLData = reader.result;
        };
        reader.readAsDataURL(passedLeadImage);
    }

    handleRetakeImage() {
        this.closeScreen.emit('retakeImage');
    }

    handleEnterDetails() {
        this.closeScreen.emit('closeScreen');
    }

    private uploadImage(file: File) {
        this.handleLeadState(LEAD_STATE.PARSING);

        // Getting hidden folder ID and Store Image to S3 and image ref is store inside that hidden folder
        this.uploadAndSummarizeImage(file).pipe(
            catchError(this.handleUploadError),
        ).subscribe((response) => this.handleSummarizeResponse(response));
    }

    private uploadAndSummarizeImage(file: any) {
        // For any case if the media is not uploaded to S3 then directly uploading the image to summarization
        // 400 status code signify that media is not uploaded to S3
        // Case 1 -> User having viewer are forbidden to upload media to S3
        // Case 2 -> Issue with puting image url to S3 bucket
        return this.cardScannerService.summarizeImageFromFile(file).pipe(
            catchError((error) => {
                console.error('Error uploading image for summarization:', error);
                return of(null);
            })
        );
    }

    // Handle error during image upload and summarize of image
    private handleUploadError(error: any) {
        console.error('An error occurred:', error);
        this.handleLeadState(LEAD_STATE.ERROR_PARSING);
        return of(null);
    }

    // Handle response after image summarization
    private handleSummarizeResponse(response: any) {
        if (!response || !response.length) {
            this.handleLeadState(LEAD_STATE.ERROR_PARSING);
            this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.INVALID);
            return;
        }

        try {
            const dataResponse = JSON.parse(response);
            this.businessCardLeadFieldForm.patchValue({
                first_name: dataResponse['first_name'],
                last_name: dataResponse['last_name'],
                email: dataResponse['email'],
                phone: dataResponse['phone'],
                designation: dataResponse['designation'],
                company: dataResponse['company'],
            });

            if (this.isAllFieldsEmpty()) {
                this.handleLeadState(LEAD_STATE.ERROR_PARSING);
                this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.INVALID);
                return;
            }

            this.handleLeadState(LEAD_STATE.EDIT);
            this.handleAfterImageProcessingState(AFTER_IMAGE_PROCESSING.SUCCESS);
            this.summarizeDataEmitter.emit(dataResponse)
        } catch (error) {
            console.error('Error parsing response:', error);
            this.handleLeadState(LEAD_STATE.ERROR_PARSING);
        }
    }

    // Check if all fields are empty
    private isAllFieldsEmpty() {
        return Object.values(this.businessCardLeadFieldForm.controls)
            .every(control => !control.value || control.value.trim() === '');
    }
}
